import React, { useEffect, useRef, useState, useMemo } from 'react';
import { Tab } from 'semantic-ui-react';
import { Rates, Hotels } from '../../api/services/next';
import { SyncRateToChannelManager } from '../../api/services/pms';
import { onError, onSuccess, onWarning } from '../../components/shared/messages';
import { useMessageResources, useTextResources } from '../../language';
import { PriceListContext } from './context';
import { General } from './General/General';
import { RateAssignments } from './RateAssignments/RateAssignments';
import { Pricing } from './Pricing';
import { Adjustments } from './Adjustments';
import { RateCategory, MenuActions, EntityType, Designations, RatePricingTypesEnum, ForbiddenCharacters } from "../../components/shared/constants"
import { getMissingFieldsFromObject } from '../../components/shared/utils'
import moment from 'moment'
import "./style.less";
import { Content } from './Content';
import { useCurrentHotel } from '../../hooks/useCurrentHotel';
import { FinalPrice } from './FinalPrice';
import { useLoader } from '../../hooks/useLoader';
import CopyPricingForm from './CopyPricingForm';

export const PriceListSettings = () => {
    const loader = useLoader();
    const currentHotel = useCurrentHotel();
    const message = useMessageResources();
    const text = useTextResources();
    const [state, setState] = useState({
        YieldLevels: 0,
        PercentAdditionPerLevel: 0,
        AmountAdditionPerLevel: 0,
        MinNights: 1,
        MaxNights: 999
    });
    const [dirtyState, setDirtyState] = useState({
        GeneralTab: false,
        PricingTab: false,
        AdjustmentsTab: false,
        RateAssignmentsTab: false
    });
    const [openCopyModal, setOpenCopyModal] = useState(false);

    /*
    Used to show the rate's name in the header -
    keeps track on previous name to avoid header changes when user alters the name, until rate is saved
    */
    const rateNameRef = useRef()
    const ratesHotelName = useRef()

    //subscribe beforeunload
    useEffect(() => {
        window.addEventListener('beforeunload', alertIfDirty);
        return () => {
            window.removeEventListener('beforeunload', alertIfDirty);
        };
    }, [dirtyState])

    const isValidBeforeSave = React.useCallback(() => {
        if (ForbiddenCharacters.some(c => state.Name.includes(c))) {
            onWarning(message("RateNameContainsForbiddenCharacters"));
            return false;
        }

        //Check for missing flat and nested fields
        const missingFields =
            getMissingFieldsFromObject(state, ['Name', 'StartDate', 'EndDate'])
        
        if(!state.BaseRateId && (state.RateCategoryId === RateCategory.Derived || state.RateCategoryId === RateCategory.DerivedPromotion)){
            missingFields.push(text("BasedOnRate"))
        }
        else if(!state.CurrencyId){
            missingFields.push(text("Currency"))
        }

        if (!state.PolicyId && state.RateCategoryId !== RateCategory.ExtraAddOn) {
            missingFields.push(text("ReservationPolicy"))
        }

        if (missingFields.length) {
            onWarning(text("MissingFields").replace('{0}', missingFields.join(", ")))
            return false;
        }

        const minMaxNightsInValid = isMinNightsSmallerThanMaxNights(state)
        if (minMaxNightsInValid) {
            onWarning(message("MinimumNightsCannotBeBiggerThanMaximumNights"))
            return false;
        }

        const minMaxLeadFixedDayInValid = isMinLeadFixedDaySmallerThanMax(state)
        if (minMaxLeadFixedDayInValid) {
            onWarning(message("MaximumBiggerThanMinimum"))
            return false;
        }

        const nonPricedRPEs = state.RatePricingElements?.find(rpe =>
            (rpe.RatePricingTypeId == RatePricingTypesEnum.PerPerson &&
                rpe.EntityTypeId == EntityType.RoomType && !rpe.IsOverride &&
                (rpe.RatePerPeople == null || rpe.RatePerPeople.length == 0)) ||
            (rpe.RatePerPeople?.indexOf(rpp =>
                (rpp.RatePerPersonPrices?.DesignationId == Designations.Single ||
                    rpp.RatePerPersonPrices?.DesignationId == Designations.Double) &&
                (!rpp.AmountBeforeTax || !rpp.AmountAfterTax)) > 0));

        if (nonPricedRPEs) {
            onWarning(text("SingleDoubleIsMendatory"));
            return false;
        }

        const nonPricedAddonRPE = state.RatePricingElements?.find(rpe =>
            rpe.EntityTypeId == EntityType.AddOn &&
            rpe.IsActive &&
            !rpe.IsOverride &&
            !rpe.IsDummy &&
            rpe.RateId === state.Id &&
            rpe.HotelAddons?.length > 0 &&
            !rpe.HotelAddons.some(ha => ha.HotelAddonsPrices?.length > 0)
        );

        if (nonPricedAddonRPE) {
            onWarning(message("TheAddonPriceCanNotBeNull"));
            return false;
        }

        return true;
    }, [state, message, text]);

    const save = React.useCallback(async () => {
        try {
            if (!isValidBeforeSave()) return;

            loader(true);

            let updatedRate = {
                ...state,
                StartDate: moment(
                    moment(state.StartDate).toDate().toISOString()
                ).toISOString(true),
                EndDate: moment(
                    moment(state.EndDate).toDate().toISOString()
                ).toISOString(true),
                //filter out fake rpes
                RatePricingElements: state?.RatePricingElements?.filter(
                    (rpe) => !rpe.IsFake
                )
            }

            if (state.RateCategoryId == RateCategory.DayUse) {
                updatedRate = {
                    ...updatedRate,
                    IsDayUse: true,
                    AllowHomeSiteDiscount: false,
                    AcceptExternalUpdates: false,
                    PreferredDisplayOnWebsite: false,
                    IsDistributeToChannelManager: false,
                    MinNights: 0,
                    MaxNights: 0,
                }
            }

            var saveRateResponse = await Rates.PutRate(updatedRate)
            if(updatedRate.ShowBaseRates)
            {
                saveRateResponse.ShowBaseRates = false
            }

            // The save rate returns a limited object, lacking all the necessary includes.
            // Therefore, we need to retain the previous object and override only the updated properties.
            // Preserve RatePricingElements from previous state since the response doesn't include them.
            // RPEsNeedRefresh signals Pricing.jsx to reload from the DB so stale Id:0 RPEs
            // (already persisted by PutRate) get replaced with real IDs before the user edits prices.
            setState((prev) => ({ ...updatedRate, ...saveRateResponse, RatePricingElements: prev.RatePricingElements, forceRefreshRPEs: true, savedCurrencyId: updatedRate.CurrencyId ?? null }));

            rateNameRef.current = updatedRate.Name;

            if (updatedRate.RateCategoryId == RateCategory.DerivedPromotion) {
                await SyncRateToChannelManager({
                    PMSHotelID: updatedRate.HotelId,
                    ExternalPMSRateID: updatedRate.Id
                })
            }

            setDirtyState({
                GeneralTab: false,
                PricingTab: false,
                AdjustmentsTab: false,
                RateAssignmentsTab: false
            });

            onSuccess();
        } catch (e) {
            switch (e.error.detail) {
                case 'RateNameAlreadyExists':
                    onWarning(
                        message(e.error.detail)
                            .replace('{0}', state.Name)
                    );
                    break;
                case 'SystemRateAlreadyExistsForTheCurrency':
                    onWarning(message(e.error.detail));
                    break;
                default:
                    onError(e?.error?.detail ?? e);
                    break;
            }
        } finally {
            loader(false);
        }
    }, [state, loader, isValidBeforeSave, message]);
    
    const copy = React.useCallback(() => {
        setOpenCopyModal(true);
    }, []);

    useEffect(() => {
        if (window.optima?.commands) {
            window.optima.commands.unsubscribe(MenuActions.save);
            window.optima.commands.subscribe(MenuActions.save, save);
        }
    }, [save, state]);

    useEffect(() => {
        if (window.optima?.commands && state.Id && state.RateCategoryId && state.RateCategoryId !== RateCategory.Derived) {
            window.optima.commands.unsubscribe(MenuActions.copy);
            window.optima.commands.subscribe(MenuActions.copy, copy);
        }
    }, [copy, state]);

    useEffect(() => {
        let urlParams = new URLSearchParams(location.search);
        let rateId = urlParams.get("RateId") || urlParams.get("rateId");
        let rateCategory = urlParams.get("RateCategory");
        if (rateId) {
            (async () => {
                try {
                    loader(true);
                    let fetchedRate = await Rates.GetRates({
                        includes: ["RateOpenForSaleConditions", "ClassificationCategoriesAssignments", "StaticDiscountMappings"],
                        Id: rateId,
                        InActive: true
                    });
                    if (fetchedRate.length === 0)
                        onWarning(message("ErrorRateNotFound").replace("{0}", rateId).replace("{1}", window.optima.user.Id))
                    else {
                        if (currentHotel.Id === fetchedRate[0].HotelId) {
                            ratesHotelName.current = currentHotel.Name
                        }
                        else {
                            const ratesHotel = await Hotels.GetHotels({
                                Id: fetchedRate[0].HotelId,
                            })
                            if (ratesHotel.length > 0)
                                ratesHotelName.current = ratesHotel[0].Name
                        }

                        setState({ ...state, ...fetchedRate[0], savedCurrencyId: fetchedRate[0].CurrencyId ?? null });
                        rateNameRef.current = fetchedRate[0].Name
                    }
                } catch (error) {
                    onError(error);
                } finally {
                    loader(false);
                }
            })()
        }
        if (rateCategory != 0) {
            setState((prev) => ({ ...prev, TypeId: rateCategory }));
        }
    }, [])

    useEffect(() => {
        if (window.optima?.commands?.registerCustomToolbarComponent) {
            if (ratesHotelName.current && rateNameRef.current) {
                window.optima.commands.registerCustomToolbarComponent(() => <h3>
                    {`- ${ratesHotelName.current} - ${rateNameRef.current}`}
                </h3>, true)
            }
        }
    }, [state.Name])

    /* Updates the translations dictionary -
   either updates existing or creates a new one under the specific languageId */

    const isMinNightsSmallerThanMaxNights = (state) =>
        parseInt(state.MinNights) > parseInt(state.MaxNights)

    const isMinLeadFixedDaySmallerThanMax = (state) =>
        parseInt(state.LeadFixedDaysStart) > parseInt(state.LeadFixedDaysEnd)

    

    

    const alertIfDirty = (event) => {
        if (Object.values(dirtyState).some(v => v == true)) {
            event.preventDefault();
            event.returnValue = '';
        }
    }

    const HideControlsByRateType = () => {
        if (!state?.RateCategoryId)
            return null
        if (state?.RateCategoryId === RateCategory.ExtraAddOn)
            return tabs;
        if ([RateCategory.Derived, RateCategory.DerivedPromotion].includes(state.RateCategoryId))
            return [...tabs, ...hiddenTabs, {
                menuItem: text("Adjustments"), render: () => <Tab.Pane style={{ height: '100%' }}>
                    <Adjustments />
                </Tab.Pane>
            }];
        return tabs.concat(hiddenTabs);
    }

    const tabs = [
        {
            menuItem: text("General"), render: () => <Tab.Pane>
                <General />
            </Tab.Pane>
        },
        {
            menuItem: text("Pricing"), render: () => <Tab.Pane className='pricing-tab'>
                <Pricing />
            </Tab.Pane>
        },
    ]
    const hiddenTabs = [
        //handle adjustment tab disable
        {
            menuItem: text("Assignments"), render: () =>
                <Tab.Pane>
                    <RateAssignments rateId={state.Id} />
                </Tab.Pane>
        },
        {
            menuItem: text("Content"), render: () => <Tab.Pane style={{ height: '100%' }}>
                <Content
                    EntityTypeId={EntityType.Rate}
                    EntityId={state.Id}
                    HotelId={state.HotelId}
                    save={save}
                />
            </Tab.Pane>
        },
        {
            menuItem: text("FinalPrice"), render: () => <Tab.Pane style={{ height: '100%' }}>
                <FinalPrice
                    HotelId={state.HotelId}
                    RateId={state.Id}
                />
            </Tab.Pane>
        },
    ]

    const contextValue = useMemo(() => ({ state, setState, dirtyState, setDirtyState }), [state, setState, dirtyState, setDirtyState]);

    return <div className="price-list-settings">
        <PriceListContext.Provider value={contextValue}>
            <Tab className="tab-container" panes={HideControlsByRateType()} menu={{ secondary: true, pointing: true }} />
        </PriceListContext.Provider>
        <CopyPricingForm open={openCopyModal} rate={{...state}} onClose={() => setOpenCopyModal(false)} rateId={state.Id} />
    </div>
}
